import { BrandManifest, DEFAULT_MANIFEST } from './manifest.schema';
import { resolvePublicAssetPath } from './publicAsset';

let currentManifestUrl: string | null = null;

function upsertMeta(name: string, content: string): void {
  let element = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute('name', name);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
}

function upsertLink(rel: string, href: string): void {
  let element = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }
  element.setAttribute('href', href);
}

function buildWebManifest(manifest: BrandManifest) {
  const title = manifest.metadata?.title || manifest.displayName || DEFAULT_MANIFEST.displayName;
  const description =
    manifest.metadata?.description || DEFAULT_MANIFEST.metadata?.description || DEFAULT_MANIFEST.displayName;
  const themeColor =
    manifest.metadata?.themeColor ||
    manifest.theme.tokens['--brand-primary'] ||
    DEFAULT_MANIFEST.metadata?.themeColor ||
    '#111827';

  return {
    name: title,
    short_name: manifest.displayName,
    description,
    start_url: '.',
    display: 'standalone',
    background_color: '#ffffff',
    theme_color: themeColor,
    icons: [
      manifest.assets.pwaIcon192
        ? { src: resolvePublicAssetPath(manifest.assets.pwaIcon192), sizes: '192x192', type: 'image/png' }
        : null,
      manifest.assets.pwaIcon512
        ? { src: resolvePublicAssetPath(manifest.assets.pwaIcon512), sizes: '512x512', type: 'image/png' }
        : null
    ].filter(Boolean)
  };
}

function updateWebManifest(manifest: BrandManifest): void {
  if (currentManifestUrl && typeof URL.revokeObjectURL === 'function') {
    URL.revokeObjectURL(currentManifestUrl);
    currentManifestUrl = null;
  }

  if (typeof Blob === 'undefined' || typeof URL.createObjectURL !== 'function') {
    upsertLink('manifest', resolvePublicAssetPath('manifest.json'));
    return;
  }

  const manifestBlob = new Blob([JSON.stringify(buildWebManifest(manifest))], {
    type: 'application/manifest+json'
  });
  currentManifestUrl = URL.createObjectURL(manifestBlob);
  upsertLink('manifest', currentManifestUrl);
}

export function applyBrowserBranding(manifest: BrandManifest): void {
  const title = manifest.metadata?.title || manifest.displayName || DEFAULT_MANIFEST.displayName;
  const description =
    manifest.metadata?.description || DEFAULT_MANIFEST.metadata?.description || DEFAULT_MANIFEST.displayName;
  const themeColor =
    manifest.metadata?.themeColor ||
    manifest.theme.tokens['--brand-primary'] ||
    DEFAULT_MANIFEST.metadata?.themeColor ||
    '#111827';

  document.title = title;
  upsertMeta('description', description);
  upsertMeta('theme-color', themeColor);
  upsertLink('icon', resolvePublicAssetPath(manifest.assets.favicon || DEFAULT_MANIFEST.assets.favicon || ''));
  upsertLink('shortcut icon', resolvePublicAssetPath(manifest.assets.favicon || DEFAULT_MANIFEST.assets.favicon || ''));
  upsertLink(
    'apple-touch-icon',
    resolvePublicAssetPath(manifest.assets.appleTouchIcon || DEFAULT_MANIFEST.assets.appleTouchIcon || '')
  );
  updateWebManifest(manifest);
}
